/**
 * Look-dev scenes (docs/spec/06 §6): `?scene=sprites` shows every sprite animated in several cave
 * palettes; `?scene=caves&set=classic&level=1` shows every cave of a set as thumbnails.
 */

import { buildCave, caveSet, playOrder } from '../caves';
import type { CaveSetId } from '../caves/types';
import { atlasFor } from '../render/atlas';
import { remasterAtlasFor } from '../render/remaster/atlas';
import { GREY, WHITE, YELLOW } from '../render/palette';
import type { Screen } from '../render/screen';
import { SPRITE_NAMES } from '../render/sprites';
import { thumbnail } from '../render/thumbnail';
import type { Scene } from '../ui/scene';

const PALETTES: readonly (readonly [number, number, number])[] = [
  [8, 12, 1],
  [6, 14, 7],
  [9, 15, 3],
  [2, 10, 7],
  [11, 13, 1],
  [4, 3, 1],
];

export function spritesScene(art: 'c64' | 'remastered' = 'c64'): Scene {
  const sheet = art === 'remastered' ? remasterAtlasFor : atlasFor;
  let t = 0;
  return {
    size: () => ({ w: 640, h: 360 }),
    update(dt) {
      t += dt;
    },
    draw(screen: Screen) {
      screen.clear(0);
      const anim = Math.floor(t / 40);
      const pal = PALETTES[Math.floor(t / 4000) % PALETTES.length] ?? [8, 12, 1];
      const big = sheet(pal);
      screen.text(`SPRITES  ${pal.join(',')}`, 8, 4, YELLOW);
      SPRITE_NAMES.forEach((name, i) => {
        const x = 8 + (i % 12) * 52;
        const y = 16 + Math.floor(i / 12) * 60;
        const c = screen.ctx;
        c.save();
        c.translate(x, y);
        c.scale(3, 3);
        big.draw(c, name, anim >> 1, 0, 0);
        c.restore();
        screen.text(name.slice(0, 6), x, y + 49, GREY);
      });
      PALETTES.forEach((p, row) => {
        const atlas = sheet(p);
        SPRITE_NAMES.forEach((name, i) => atlas.draw(screen.ctx, name, anim >> 1, 8 + i * 18, 200 + row * 18));
      });
      screen.text('ANIMATION CLOCK 25 HZ', 8, 350, WHITE);
    },
  };
}

export function cavesScene(set: CaveSetId, level: number): Scene {
  return {
    size: () => ({ w: 640, h: 360 }),
    update() {},
    draw(screen: Screen) {
      screen.clear(0);
      screen.text(`${caveSet(set).name} LEVEL ${level}`, 8, 4, YELLOW);
      playOrder(caveSet(set)).forEach((def, i) => {
        const x = 8 + (i % 5) * 126;
        const y = 18 + Math.floor(i / 5) * 86;
        const b = buildCave(def, level);
        const px = def.intermission ? 4 : 3;
        screen.image(thumbnail(b, px), x, y + 10);
        screen.text(`${def.id} ${def.name}`.slice(0, 15), x, y, def.intermission ? YELLOW : WHITE);
      });
    },
  };
}
